import { useMemo } from 'react'

import { TransactionStatus } from '#/shared/api/generated/billing/models'
import type {
  PaymentDetail,
  PaymentMethodBrief,
  PaymentPage,
  PaymentTransactionBrief,
} from '#/shared/api/generated/billing/models'
import {
  useGetPaymentApiV1PaymentsPaymentIdGet,
  useListPaymentsApiV1PaymentsGet,
} from '#/shared/api/generated/billing/theCargoBilling'
import { formatNoteTimestamp, formatUsd } from '#/shared/lib/format'
import {
  PAYMENT_DIRECTION,
  PAYMENT_METHOD,
  PAYMENT_TYPE,
} from '#/shared/ui/create-payment-modal'
import type {
  PaymentDirection,
  PaymentMethod,
  PaymentType,
} from '#/shared/ui/create-payment-modal'

const DEFAULT_PAYMENT_PAGE_SIZE = 100

export const PAYMENT_LIST_STATUS = {
  created: 'created',
  pending: 'pending',
  paid: 'paid',
  partiallyPaid: 'partially_paid',
  refunded: 'refunded',
  canceled: 'canceled',
  failed: 'failed',
} as const

export type PaymentListStatus =
  (typeof PAYMENT_LIST_STATUS)[keyof typeof PAYMENT_LIST_STATUS]

export type PaymentBadgeVariant = 'default' | 'success' | 'danger' | 'warning'

export interface ShipmentPaymentBreakdown {
  amount: string
  surcharge: string
  discount: string
  totalLabel: 'totalCharge' | 'totalPay'
  total: string
  chargedLabel: 'charged' | 'paid'
  charged: string
  chargedVariant: PaymentBadgeVariant
}

export interface ShipmentPaymentItem {
  id: string
  paymentType: PaymentType
  direction: PaymentDirection | undefined
  method: PaymentMethod | undefined
  status: string
  isPaid: boolean
  isCardCharge: boolean
  cardLast4: string | undefined
  amountFormatted: string
  breakdown: ShipmentPaymentBreakdown
  flow: {
    direction: PaymentDirection | undefined
    variant: PaymentBadgeVariant
  }
  arrow: 'down' | 'up'
  arrowVariant: PaymentBadgeVariant
  methodLabel: string | undefined
  timestamp: string | undefined
  statusLabel: string
  statusVariant: PaymentBadgeVariant
  metaLabel: string | undefined
  receiptEmail: string | undefined
  sentInvoiceAt: string | undefined
  receiptTransactionId: string | undefined
  invoiceLink: string | undefined
  stripeInvoiceUrl: string | undefined
}

export interface ShipmentPaymentTotals {
  total: string
  collected: string | undefined
  refunded: string | undefined
  paidToCarrier: string | undefined
}

export interface UseShipmentPaymentsResult {
  payments: ShipmentPaymentItem[]
  totals: ShipmentPaymentTotals | undefined
  isLoading: boolean
}

interface UseShipmentPaymentsOptions {
  enabled?: boolean
  size?: number
}

const PAYMENT_TYPE_VALUES = new Set<PaymentType>(Object.values(PAYMENT_TYPE))
const PAYMENT_DIRECTION_VALUES = new Set<PaymentDirection>(
  Object.values(PAYMENT_DIRECTION),
)
const PAYMENT_METHOD_VALUES = new Set<PaymentMethod>(
  Object.values(PAYMENT_METHOD),
)

const PAID_STATUSES: ReadonlySet<string> = new Set([
  PAYMENT_LIST_STATUS.paid,
  PAYMENT_LIST_STATUS.refunded,
])

interface PaymentSource {
  id: string
  payment_type?: string | null
  direction?: string | null
  method_type?: string | null
  status: string
  amount: number
  total_amount: number
  discount: number
  surcharge_amount: number
  amount_charged: number
  card_last4?: string | null
  paid_date?: string | null
  created_at?: string | null
  sent_invoice_at?: string | null
  invoice_link?: string | null
  stripe_invoice_url?: string | null
  transactions?: PaymentTransactionBrief[]
}

function parseAmount(raw: number | string | null | undefined): number {
  if (raw == null) return 0
  const value = Number(raw)
  return Number.isFinite(value) ? value : 0
}

function formatMoney(value: number): string {
  return formatUsd(value, { showCents: true })
}

function formatDiscount(value: number): string {
  if (value <= 0) return formatMoney(0)
  return `(${formatMoney(value)})`
}

function isPaymentType(value: string | null | undefined): value is PaymentType {
  return Boolean(value) && PAYMENT_TYPE_VALUES.has(value as PaymentType)
}

function isPaymentDirection(
  value: string | null | undefined,
): value is PaymentDirection {
  return (
    Boolean(value) && PAYMENT_DIRECTION_VALUES.has(value as PaymentDirection)
  )
}

function isPaymentMethod(
  value: string | null | undefined,
): value is PaymentMethod {
  return Boolean(value) && PAYMENT_METHOD_VALUES.has(value as PaymentMethod)
}

function pickPaymentType(item: PaymentSource): PaymentType {
  return isPaymentType(item.payment_type)
    ? item.payment_type
    : PAYMENT_TYPE.income
}

function pickDirection(item: PaymentSource): PaymentDirection | undefined {
  return isPaymentDirection(item.direction) ? item.direction : undefined
}

function pickMethod(item: PaymentSource): PaymentMethod | undefined {
  return isPaymentMethod(item.method_type) ? item.method_type : undefined
}

function variantForType(type: PaymentType): PaymentBadgeVariant {
  if (type === PAYMENT_TYPE.refund) return 'danger'
  if (type === PAYMENT_TYPE.carrier_pay) return 'warning'
  return 'success'
}

function arrowForType(type: PaymentType): 'down' | 'up' {
  return type === PAYMENT_TYPE.income ? 'down' : 'up'
}

function totalLabelForType(
  type: PaymentType,
): ShipmentPaymentBreakdown['totalLabel'] {
  return type === PAYMENT_TYPE.income ? 'totalCharge' : 'totalPay'
}

function chargedLabelForType(
  type: PaymentType,
): ShipmentPaymentBreakdown['chargedLabel'] {
  return type === PAYMENT_TYPE.income ? 'charged' : 'paid'
}

function methodLabel(method: PaymentMethod | undefined): string | undefined {
  if (!method) return undefined
  switch (method) {
    case PAYMENT_METHOD.zelle:
      return 'Zelle'
    case PAYMENT_METHOD.ach:
      return 'ACH'
    case PAYMENT_METHOD.wire:
      return 'Wire'
    case PAYMENT_METHOD.card:
      return 'Card'
    case PAYMENT_METHOD.check:
      return 'Check'
    case PAYMENT_METHOD.cash:
      return 'Cash'
    default:
      return undefined
  }
}

function pickReceiptTransactionId(item: PaymentSource): string | undefined {
  const succeeded = (item.transactions ?? []).filter(
    (tx) => tx.status === TransactionStatus.succeeded,
  )
  if (succeeded.length === 0) return undefined
  return succeeded.reduce((latest, tx) =>
    tx.created_at > latest.created_at ? tx : latest,
  ).id
}

function pickTimestamp(item: PaymentSource): string | undefined {
  const iso = item.paid_date ?? item.created_at
  if (!iso) return undefined
  return formatNoteTimestamp(iso)
}

function methodDisplay(
  method: string | undefined,
  cardLast4: string | undefined,
): string | undefined {
  if (!method) return undefined
  return cardLast4 ? `${method} ···· ${cardLast4}` : method
}

function statusInfo(
  status: string,
  method: string | undefined,
  cardLast4: string | undefined,
  timestamp: string | undefined,
): { label: string; variant: PaymentBadgeVariant } {
  const display = methodDisplay(method, cardLast4)
  const tail = display && timestamp ? ` · ${display} on ${timestamp}` : ''
  switch (status) {
    case PAYMENT_LIST_STATUS.paid:
      return { label: `Paid${tail}`, variant: 'success' }
    case PAYMENT_LIST_STATUS.partiallyPaid:
      return { label: `Partially paid${tail}`, variant: 'warning' }
    case PAYMENT_LIST_STATUS.refunded:
      return { label: `Refunded${tail}`, variant: 'danger' }
    case PAYMENT_LIST_STATUS.failed:
      return { label: 'Failed', variant: 'danger' }
    case PAYMENT_LIST_STATUS.canceled:
      return { label: 'Canceled', variant: 'default' }
    default:
      return { label: 'Awaiting payment', variant: 'warning' }
  }
}

function toBreakdown(
  item: PaymentSource,
  type: PaymentType,
  isPaid: boolean,
  variant: PaymentBadgeVariant,
): ShipmentPaymentBreakdown {
  const amount = parseAmount(item.amount)
  const total = parseAmount(item.total_amount)
  const discount = parseAmount(item.discount)
  const surcharge = parseAmount(item.surcharge_amount)
  const charged = parseAmount(item.amount_charged)
  return {
    amount: formatMoney(amount),
    surcharge: formatMoney(surcharge),
    discount: formatDiscount(discount),
    totalLabel: totalLabelForType(type),
    total: formatMoney(total),
    chargedLabel: chargedLabelForType(type),
    charged: formatMoney(charged),
    chargedVariant: isPaid ? variant : 'default',
  }
}

function toShipmentPaymentItem(item: PaymentSource): ShipmentPaymentItem {
  const type = pickPaymentType(item)
  const direction = pickDirection(item)
  const method = pickMethod(item)
  const status = item.status
  const isPaid = PAID_STATUSES.has(status)
  const variant = variantForType(type)
  const label = methodLabel(method)
  const cardLast4 = item.card_last4 ?? undefined
  const timestamp = pickTimestamp(item)
  const { label: statusLabel, variant: statusVariant } = statusInfo(
    status,
    label,
    cardLast4,
    timestamp,
  )
  return {
    id: item.id,
    paymentType: type,
    direction,
    method,
    status,
    isPaid,
    isCardCharge: method === PAYMENT_METHOD.card,
    cardLast4,
    amountFormatted: formatMoney(parseAmount(item.amount)),
    breakdown: toBreakdown(item, type, isPaid, variant),
    flow: { direction, variant },
    arrow: arrowForType(type),
    arrowVariant: variant,
    methodLabel: label,
    timestamp,
    statusLabel,
    statusVariant,
    metaLabel: timestamp && (isPaid ? timestamp : `Created ${timestamp}`),
    receiptEmail: undefined,
    sentInvoiceAt: item.sent_invoice_at ?? undefined,
    receiptTransactionId: pickReceiptTransactionId(item),
    invoiceLink: item.invoice_link ?? undefined,
    stripeInvoiceUrl: item.stripe_invoice_url ?? undefined,
  }
}

function toTotals(
  page: PaymentPage | undefined,
): ShipmentPaymentTotals | undefined {
  if (!page) return undefined
  const totals = page.totals
  return {
    total: formatMoney(parseAmount(totals.total)),
    collected:
      totals.collected !== undefined
        ? formatMoney(parseAmount(totals.collected))
        : undefined,
    refunded:
      totals.refunded !== undefined
        ? formatMoney(parseAmount(totals.refunded))
        : undefined,
    paidToCarrier:
      totals.paid_to_carrier !== undefined
        ? formatMoney(parseAmount(totals.paid_to_carrier))
        : undefined,
  }
}

export function useShipmentPayments(
  shipmentId: string | undefined,
  {
    enabled = true,
    size = DEFAULT_PAYMENT_PAGE_SIZE,
  }: UseShipmentPaymentsOptions = {},
): UseShipmentPaymentsResult {
  const armed = enabled && Boolean(shipmentId)
  const query = useListPaymentsApiV1PaymentsGet(
    { shipment_id: shipmentId ?? '', size },
    { query: { enabled: armed } },
  )

  const page = query.data?.data
  const payments = useMemo<ShipmentPaymentItem[]>(() => {
    return (page?.items ?? []).map(toShipmentPaymentItem)
  }, [page])

  return {
    payments,
    totals: toTotals(page),
    isLoading: query.isLoading && armed,
  }
}

export interface UsePaymentItemResult {
  item: ShipmentPaymentItem
  methods: ReadonlyArray<PaymentMethodBrief>
  isLoading: boolean
}

export function usePaymentItem(
  paymentId: string,
  fallback: ShipmentPaymentItem,
  enabled: boolean,
): UsePaymentItemResult {
  const query = useGetPaymentApiV1PaymentsPaymentIdGet(paymentId, {
    query: { enabled: enabled && Boolean(paymentId) },
  })
  const detail: PaymentDetail | undefined = query.data?.data
  const item = useMemo(
    () => (detail ? toShipmentPaymentItem(detail) : fallback),
    [detail, fallback],
  )
  return {
    item,
    methods: detail?.methods ?? [],
    isLoading: query.isLoading && enabled,
  }
}
